
import { Module } from '../types';

export const ERROR_HANDLING_MODULE: Module = {
  id: "intermediate-4",
  title: "Error Handling in React",
  description: "Learn how to handle errors gracefully in React applications",
  duration: "70 minutes",
  difficulty: "intermediate", // Add missing difficulty
  content: `
# Error Handling in React

Learn comprehensive strategies for handling errors in React applications to create robust user experiences.

## Error Boundaries

React components that catch JavaScript errors anywhere in their child component tree.

## Try-Catch in Event Handlers

Handle errors in event handlers and async operations.

## Error Reporting

Implement error logging and reporting strategies.

## Recovery Strategies

Help users recover from error states gracefully.
  `,
  codeExamples: [
    {
      title: "Error Boundary Component",
      code: `import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // You can log the error to an error reporting service here
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-fallback">
          <h2>Something went wrong</h2>
          <p>We're sorry, but something unexpected happened.</p>
          <button onClick={() => this.setState({ hasError: false })}>
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Usage
function App() {
  return (
    <ErrorBoundary>
      <MyComponent />
    </ErrorBoundary>
  );
}`,
      explanation: "Error boundary component that catches and handles errors in child components.",
      language: "jsx"
    }
  ],
  practice: {
    task: "Create an error handling system for a user profile component",
    starter: `import React, { useState, useEffect } from 'react';

function UserProfile({ userId }) {
  // TODO: Add error handling for user data fetching
}

export default UserProfile;`,
    solution: `import React, { useState, useEffect } from 'react';

function UserProfile({ userId }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(\`/api/users/\${userId}\`);
        
        if (!response.ok) {
          throw new Error(\`Failed to fetch user: \${response.status}\`);
        }
        
        const userData = await response.json();
        setUser(userData);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching user:', err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    }
  }, [userId]);

  if (loading) return <div>Loading user...</div>;
  
  if (error) {
    return (
      <div className="error-state">
        <h3>Error loading user</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }
  
  if (!user) return <div>No user found</div>;

  return (
    <div className="user-profile">
      <h2>{user.name}</h2>
      <p>{user.email}</p>
    </div>
  );
}

export default UserProfile;`,
    hints: [
      "Use try-catch blocks for async operations",
      "Maintain error state alongside loading state",
      "Provide helpful error messages to users",
      "Include retry functionality when appropriate"
    ]
  }
};
